"use client"

import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { Pause, Play } from "lucide-react"
import { useEffect, useRef, useState } from "react"

const AUTO_ADVANCE_MS = 10_000
const PROGRESS_TICK_MS = 50

type HeroTextSlide = {
  headline: string
  subtext: string
  ctaLabel: string
}

type HomeHeroTextSliderProps = {
  slides: HeroTextSlide[]
  ctaHref?: string
  poster?: string
  videoSources?: string[]
}

export function HomeHeroTextSlider({
  slides,
  ctaHref = "tel:2692541211",
  poster,
  videoSources = [],
}: HomeHeroTextSliderProps) {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const [progress, setProgress] = useState(0)
  const elapsedRef = useRef(0)
  const lastTickRef = useRef<number | null>(null)
  const reduceMotion = useReducedMotion()

  const activeVideo = videoSources.length > 0 ? videoSources[index % videoSources.length] : undefined

  useEffect(() => {
    if (paused || slides.length <= 1) {
      lastTickRef.current = null
      return
    }

    lastTickRef.current = performance.now()
    const interval = window.setInterval(() => {
      const now = performance.now()
      const previousTick = lastTickRef.current ?? now
      elapsedRef.current += now - previousTick
      lastTickRef.current = now

      const nextProgress = Math.min(elapsedRef.current / AUTO_ADVANCE_MS, 1)

      if (nextProgress >= 1) {
        elapsedRef.current = 0
        lastTickRef.current = now
        setProgress(0)
        setIndex((current) => (current + 1) % slides.length)
        return
      }

      setProgress(nextProgress)
    }, PROGRESS_TICK_MS)

    return () => window.clearInterval(interval)
  }, [paused, slides.length, videoSources.length])

  if (slides.length === 0) return null

  const active = slides[index] ?? slides[0]
  const goTo = (nextIndex: number) => {
    elapsedRef.current = 0
    lastTickRef.current = paused ? null : performance.now()
    setProgress(0)
    const normalizedIndex = (nextIndex + slides.length) % slides.length
    setIndex(normalizedIndex)
  }

  return (
    <div
      className="relative isolate min-h-[calc(100svh-6rem)] w-full overflow-hidden text-primary-foreground lg:min-h-[calc(100vh-7rem)]"
      data-hero-text-slider="true"
      role="region"
      aria-label="Healing perspectives"
      aria-roledescription="carousel"
      aria-live="polite"
      aria-atomic="true"
    >
      {activeVideo && (
        <video
          key={activeVideo}
          aria-hidden="true"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={poster}
          src={activeVideo}
          className="absolute inset-0 -z-20 h-full w-full object-cover"
        />
      )}
      <div aria-hidden="true" className="absolute inset-0 -z-10 bg-[linear-gradient(135deg,hsl(var(--primary)/0.84),hsl(var(--primary)/0.62)_52%,hsl(var(--foreground)/0.56))]" />
      <div aria-hidden="true" className="pointer-events-none absolute left-[-12%] top-16 -z-10 h-[28rem] w-[28rem] rounded-full bg-primary/28 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute right-[-10%] top-28 -z-10 h-[34rem] w-[34rem] rounded-full bg-primary-glow/22 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute bottom-[-8%] left-[26%] -z-10 h-[24rem] w-[24rem] rounded-full bg-primary/28 blur-3xl" />
      <div className="relative z-10 mx-auto max-w-theme px-5 py-16 sm:py-20 lg:px-8 lg:py-24">
        <div className="scroll-story-rise relative z-10 max-w-6xl">
          <div className="mt-8 w-full max-w-6xl">
            <div className="relative min-h-[36rem] sm:min-h-[28rem] lg:min-h-[36rem]">
              <AnimatePresence initial={false} mode="sync">
                <motion.div
                  key={active.headline}
                  initial={reduceMotion ? { opacity: 1 } : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: reduceMotion ? 0 : 0.45, ease: "easeInOut" }}
                  className="absolute left-0 top-0 w-full lg:w-[70%]"
                >
                  <h1 className="mt-4 max-w-6xl font-heading text-5xl font-semibold leading-[0.9] tracking-tight text-primary-foreground sm:text-6xl lg:text-7xl xl:text-8xl">
                    {active.headline}
                  </h1>
                  <p className="mt-7 max-w-5xl text-lg leading-8 text-primary-foreground/90 sm:text-xl">
                    {active.subtext}
                  </p>
                  <a
                    href={ctaHref}
                    className="mt-8 inline-flex rounded-full bg-primary-foreground px-7 py-4 text-center text-sm font-semibold text-primary shadow-theme-elegant transition-[background-color,box-shadow,transform] duration-300 hover:bg-primary-foreground/90 hover:shadow-theme-glow focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-4 focus:ring-offset-primary active:scale-[0.98]"
                  >
                    {active.ctaLabel}
                  </a>
                </motion.div>
              </AnimatePresence>
            </div>

            <div
              className="mb-4 mt-8 h-1.5 w-full overflow-hidden rounded-full bg-primary-foreground/20"
              role="progressbar"
              aria-label={`Slide ${index + 1} progress`}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress * 100)}
            >
              <div
                className="h-full origin-left rounded-full bg-primary-foreground transition-transform duration-75 ease-linear"
                style={{ transform: `scaleX(${progress})` }}
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 border-t border-primary-foreground/20 pt-4">
              <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Choose a healing perspective">
                {slides.map((slide, slideIndex) => (
                  <button
                    key={slide.headline}
                    type="button"
                    role="tab"
                    aria-label={`Show slide ${slideIndex + 1}`}
                    aria-selected={slideIndex === index}
                    className={`h-2 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-primary ${slideIndex === index ? "w-8 bg-primary-foreground" : "w-2 bg-primary-foreground/40 hover:bg-primary-foreground/70"}`}
                    onClick={() => goTo(slideIndex)}
                  />
                ))}
              </div>
              <button
                type="button"
                className="inline-flex shrink-0 items-center gap-2 rounded-full border border-primary-foreground/25 bg-primary-foreground/10 px-3 py-2 text-xs font-semibold text-primary-foreground transition hover:bg-primary-foreground/20 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-primary"
                aria-label={paused ? "Play healing perspectives" : "Pause healing perspectives"}
                onClick={() => setPaused((current) => !current)}
              >
                {paused ? <Play aria-hidden="true" className="size-3.5" /> : <Pause aria-hidden="true" className="size-3.5" />}
                <span className="sr-only">{paused ? "Play" : "Pause"}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
